import { useState } from 'react'
import { useNavigate } from 'react-router'
import { GripVertical, ListChecks } from 'lucide-react'
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { catalog as api } from '@/api/endpoints'
import type { Flavor, Image } from '@/api/types'
import { Err } from '@/components/Err'
import { ExpertLine } from '@/components/Expert'
import { ToneBadge } from '@/components/ToneBadge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Empty, PageHead, Section, SectionCrumb } from '@/components/layout/Page'
import { usePoll } from '@/hooks/usePoll'
import { coresText, diskText, gpuText, memoryText } from '@/domain/flavor'
import { ACTION, ACTION_DANGER, ACTION_PLAIN } from './vm/chips'
import { matchAny } from './model'
import { FlavorCreateForm, FlavorDeleteForm } from './FlavorForms'

const POLL_MS = 30000

// 사용자가 고르는 것: 사양과 이미지. 이미지의 volid 는 전문가에게만 보인다.
//
// **사양의 조작은 팀·직위 화면처럼 제목 줄의 칩이다**(F-105) — `사양 생성` 과, 고른 사양의 `신청 가능`·`신청 불가`·`삭제`.
// 일괄은 하나씩 보내고 거절된 것만 선택에 남는다. 줄을 누르면 상세(`FlavorView.tsx`)로 가고 이름 고치기는 거기 있다.
// 신청할 수 있는 사양과 없는 사양은 표가 다르다 — 사용자가 보는 목록이 위의 표 그대로다.
//
// **순서는 끌어서 정한다.** 맨 왼쪽 손잡이로 표 안에서 옮기고(키보드는 손잡이에서 Space·화살표), 놓으면 두 표를 이은
// 전체 순서를 한 번에 보낸다(`setFlavorOrder`) — 자리 숫자를 적는 칸은 없다. 서버가 그 순서대로 주므로 화면은
// 다시 세우지 않는다: VM 생성 페이지가 같은 순서의 첫 사양을 골라 두므로, 여기서 다시 세우면 관리자가 사용자의
// 기본 사양을 이 표에서 알아볼 수 없다. 검색 중에는 보이는 줄이 전체가 아니라 손잡이가 서지 않는다.
//
// **카드는 여기서 정하지 않는다**(F-96): 신청하는 사람이 사양과 따로 모델과 장 수를 고르고,
// VM 생성 페이지가 그 카드의 메모리 하한을 채우는 사양만 고르게 한다. 그 전에 만든 사양의 카드는 목록의
// GPU 칸이 말한다 — 그 사양을 고르고 카드를 고르지 않은 신청이 받는 카드다.
//
// **이미지는 시스템이 만든다**(F-104) — 손으로 등록하는 길은 없다. 무엇을 적을지 관리자가 알 수 없는 칸(volid)이었다.
// 그 전에 손으로 등록된 행만 `제거` 가 서고, 시스템 이미지는 끄는 것으로 물린다(`managed`).
// 손 등록 행의 제거 거절(그 이미지로 신청한 VM 이 있다 — 반려된 신청도)은 서버가 사유로 말하고 창이 그 문장을 보인다.

type Act = { kind: 'create' } | { kind: 'remove'; ids: string[] } | { kind: 'remove-image'; id: string } | null

export default function Catalog({ query }: { query: string }) {
  const flavors = usePoll(() => api.flavors(), POLL_MS)
  const images = usePoll(() => api.images(), POLL_MS)
  // 디스크는 유한한 후보 집합이고 그 목록의 유일한 출처가 여기다.
  const catalog = usePoll(() => api.get(), POLL_MS)
  const nav = useNavigate()
  const [act, setAct] = useState<Act>(null)
  const [picked, setPicked] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  // 놓은 순서는 다음 읽기가 올 때까지 그 자리에 선다 — 그 읽기(`base` 와 다른 목록)가 오면 서버의 순서로 돌아간다.
  const [moved, setMoved] = useState<{ base: Flavor[]; ids: string[] } | null>(null)

  const served = flavors.data ?? []
  const all = moved && moved.base === flavors.data ? moved.ids.flatMap((id) => served.filter((f) => f.id === id)) : served
  const on = all.filter((f) => f.enabled)
  const off = all.filter((f) => !f.enabled)
  const shown = (list: Flavor[]) => list.filter((f) => matchAny([f.display_name, f.name], query))
  const is = (images.data ?? []).filter((i) => matchAny([i.name, i.os_family], query))

  // 고른 줄이 검색으로 사라지면 대상에서도 빠진다 — 보이지 않는 사양에 조작하지 않는다.
  const chosen = shown(all).filter((f) => picked.includes(f.id))
  const pick = (id: string, yes: boolean) => setPicked((v) => (yes ? (v.includes(id) ? v : [...v, id]) : v.filter((x) => x !== id)))

  const close = () => setAct(null)
  const done = () => {
    close()
    flavors.refresh()
  }
  const imageDone = () => {
    close()
    images.refresh()
  }
  const removed = () => {
    setPicked([])
    done()
  }
  // 하나씩 보내고 거절된 사양만 선택에 남는다 — 같은 칩이 곧 재시도다.
  const setEnabled = async (enabled: boolean) => {
    setBusy(true)
    setError(null)
    let first: unknown = null
    const failed: string[] = []
    for (const f of chosen) {
      try {
        await api.patchFlavor(f.id, { enabled })
      } catch (e) {
        if (first === null) first = e
        failed.push(f.id)
      }
    }
    setError(first)
    setPicked(failed)
    setBusy(false)
    flavors.refresh()
  }
  const reorder = async (ids: string[]) => {
    setMoved({ base: served, ids })
    setError(null)
    try {
      await api.setFlavorOrder(ids)
    } catch (e) {
      setError(e)
      setMoved(null)
    }
    flavors.refresh()
  }
  // 두 표를 이은 것이 전체 순서다 — 신청 가능한 사양이 앞이다.
  const moveOn = (ids: string[]) => void reorder([...ids, ...off.map((f) => f.id)])
  const moveOff = (ids: string[]) => void reorder([...on.map((f) => f.id), ...ids])

  // 대상은 id 로 든다 — 폴링이 목록을 갈아도 옛 값을 보지 않고, 모두 사라지면 대화 상자가 닫힌다.
  const doomed = act?.kind === 'remove' ? served.filter((f) => act.ids.includes(f.id)) : []
  const removing = act?.kind === 'remove-image' ? ((images.data ?? []).find((i) => i.id === act.id) ?? null) : null
  const asking = act?.kind === 'create' || doomed.length > 0 || removing !== null
  const table = (list: Flavor[], onMove: (ids: string[]) => void, empty: string) => {
    const rows = shown(list)
    return rows.length === 0 ? (
      <Empty>{query ? '검색 결과 없음' : empty}</Empty>
    ) : (
      <FlavorTable rows={rows} draggable={query === '' && !busy} picked={picked} onPick={pick} onOpen={(id) => nav(`/catalog/flavors/${id}`)} onMove={onMove} />
    )
  }

  return (
    <>
      <SectionCrumb />
      <PageHead
        icon={<ListChecks className="size-5" />}
        title="카탈로그"
        facts={`사양 ${served.length}개${chosen.length > 0 ? ` · ${chosen.length}개 선택` : ''} · 이미지 ${images.data?.length ?? 0}개`}
        right={
          <div className="mt-0.5 flex flex-wrap items-center justify-end gap-1.5">
            <button type="button" className={`${ACTION} ${ACTION_PLAIN}`} onClick={() => setAct({ kind: 'create' })}>
              사양 생성
            </button>
            <button type="button" className={`${ACTION} ${ACTION_PLAIN}`} disabled={chosen.length === 0 || busy} onClick={() => void setEnabled(true)}>
              신청 가능
            </button>
            <button type="button" className={`${ACTION} ${ACTION_PLAIN}`} disabled={chosen.length === 0 || busy} onClick={() => void setEnabled(false)}>
              신청 불가
            </button>
            <button
              type="button"
              className={`${ACTION} ${ACTION_DANGER}`}
              disabled={chosen.length === 0 || busy}
              onClick={() => setAct({ kind: 'remove', ids: chosen.map((f) => f.id) })}
            >
              삭제
            </button>
          </div>
        }
      />

      <Err error={flavors.error} />
      <Err error={error} />

      <Section title="신청 가능">{table(on, moveOn, '사양 없음')}</Section>
      <Section title="신청 불가">{table(off, moveOff, '사양 없음')}</Section>

      <Section title="이미지">
        <Err error={images.error} />
        {is.length === 0 ? (
          <Empty>{query ? '검색 결과 없음' : '이미지 없음'}</Empty>
        ) : (
          <div className="overflow-hidden rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>이름</TableHead>
                  <TableHead className="w-24 text-right">신청 가능</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {is.map((i) => (
                  <TableRow key={i.id} className="hover:bg-transparent">
                    <TableCell className="font-medium max-sm:basis-full">
                      {i.name}
                      <ExpertLine mono items={[i.volid, i.storage]} />
                    </TableCell>
                    <TableCell className="text-right">
                      <ToneBadge tone={i.enabled ? 'ok' : 'none'}>{i.enabled ? '예' : '아니오'}</ToneBadge>
                    </TableCell>
                    <TableCell className="text-right">
                      {i.managed ? null : (
                        <button className={`${ACTION} ${ACTION_DANGER}`} onClick={() => setAct({ kind: 'remove-image', id: i.id })}>
                          제거
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>

      <Dialog open={asking} onOpenChange={(v) => (v ? undefined : close())}>
        <DialogContent aria-describedby={undefined}>
          {act?.kind === 'create' ? <FlavorCreateForm steps={catalog.data?.disk_steps ?? []} onDone={done} /> : null}
          {act?.kind === 'remove' && doomed.length > 0 ? (
            <FlavorDeleteForm flavors={doomed} onClose={close} onDone={removed} onRefused={flavors.refresh} />
          ) : null}
          {removing ? <RemoveImageForm image={removing} onClose={close} onDone={imageDone} /> : null}
        </DialogContent>
      </Dialog>
    </>
  )
}

/** 사양 한 표. 손잡이로 표 안에서 옮기고, 놓으면 그 표의 새 순서를 `onMove` 로 넘긴다. */
function FlavorTable({
  rows,
  draggable,
  picked,
  onPick,
  onOpen,
  onMove,
}: {
  rows: Flavor[]
  draggable: boolean
  picked: string[]
  onPick: (id: string, on: boolean) => void
  onOpen: (id: string) => void
  onMove: (ids: string[]) => void
}) {
  // 손잡이를 조금 끌어야 옮기기가 시작된다 — 손잡이를 누르기만 한 것은 끌기가 아니다.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const ids = rows.map((f) => f.id)
  const end = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    onMove(arrayMove(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id))))
  }
  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={end}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-8" />
                <TableHead className="w-8" />
                <TableHead>이름</TableHead>
                <TableHead className="w-24">코어</TableHead>
                <TableHead className="w-24">메모리</TableHead>
                <TableHead className="w-24">디스크</TableHead>
                <TableHead className="w-36">GPU</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((f) => (
                <FlavorRow key={f.id} flavor={f} draggable={draggable} picked={picked.includes(f.id)} onPick={onPick} onOpen={() => onOpen(f.id)} />
              ))}
            </TableBody>
          </Table>
        </SortableContext>
      </DndContext>
    </div>
  )
}

function FlavorRow({
  flavor: f,
  draggable,
  picked,
  onPick,
  onOpen,
}: {
  flavor: Flavor
  draggable: boolean
  picked: boolean
  onPick: (id: string, on: boolean) => void
  onOpen: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: f.id, disabled: !draggable })
  return (
    <TableRow
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      onClick={onOpen}
      className={`cursor-pointer hover:bg-surface-2 ${isDragging ? 'relative z-10 bg-surface-2' : ''}`}
    >
      <TableCell onClick={(e) => e.stopPropagation()}>
        {draggable ? (
          <button
            type="button"
            aria-label={`${f.display_name} 옮기기`}
            className="flex cursor-grab touch-none items-center text-muted-foreground hover:text-foreground"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="size-4" />
          </button>
        ) : null}
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Checkbox aria-label={`${f.display_name} 선택`} checked={picked} onCheckedChange={(on) => onPick(f.id, on === true)} />
      </TableCell>
      <TableCell className="font-medium max-sm:basis-full">
        {f.display_name}
        <ExpertLine mono items={[f.name]} />
      </TableCell>
      {/* 자유로 둔 자원은 값이 없는 것이 아니라 신청하는 사람이 고르는 것이다(F-62). */}
      <TableCell className="tabular-nums">{coresText(f)}</TableCell>
      <TableCell className="tabular-nums">{memoryText(f)}</TableCell>
      <TableCell className="tabular-nums">{diskText(f)}</TableCell>
      {/* 카드는 그 전에 만든 사양만 말한다 — 카드를 고르지 않은 신청이 받는 카드다. 자유가 없어 값이거나 없는 값이다. */}
      <TableCell className="tabular-nums">{gpuText(f)}</TableCell>
    </TableRow>
  )
}

/** 제거 창: 결과 한 문장과 제거. 거절되면 서버의 사유가 그 이유를 한 문장으로 말한다. */
function RemoveImageForm({ image, onClose, onDone }: { image: Image; onClose: () => void; onDone: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const remove = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.deleteImage(image.id)
      onDone()
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }
  return (
    <>
      <DialogHeader>
        <DialogTitle>{image.name} 제거</DialogTitle>
      </DialogHeader>
      <p className="text-[13px]">이미지가 목록에서 사라집니다.</p>
      <Err error={error} />
      <DialogFooter>
        <Button variant="outline" disabled={busy} onClick={onClose}>
          취소
        </Button>
        <Button variant="destructive" disabled={busy} onClick={() => void remove()}>
          {busy ? '제거 중' : '제거'}
        </Button>
      </DialogFooter>
    </>
  )
}
